import React, { createContext, useContext, useEffect, useState } from 'react'

import { BillingInfo, Plan, Feature } from '@/types/billing'
import { useMantle } from '@heymantle/react'

interface BillingContextType {
    billingInfo: BillingInfo | null
    isLoading: boolean
    currentPlan: Plan | null
    isFeatureEnabled: (featureId: string) => boolean
    getFeatureLimit: (featureId: string) => number | null
    shouldShowUpgradePrompt: (featureId: string) => boolean
}

const BillingContext = createContext<BillingContextType | undefined>(undefined)

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { subscription } = useMantle()

    const isFeatureEnabled = (featureId: string): boolean => {
        if (!subscription) return false

        const feature = subscription.plan.features[featureId]
        if (!feature) return false

        if (feature.type === 'boolean') {
            return feature.value === true
        }

        return (
            typeof feature.value === 'number' &&
            (feature.value === -1 || feature.value > 0)
        )
    }

    const getFeatureLimit = (featureId: string): number | null => {
        if (!subscription) return null
        const feature = subscription.plan.features[featureId]

        if (!feature || feature.type !== 'limit') return null

        return typeof feature.value === 'number' ? feature.value : null
    }

    const shouldShowUpgradePrompt = (featureId: string): boolean => {
        if (!subscription) return false

        // If user is on free plan and feature is not enabled, show upgrade prompt
        if (subscription.plan.amount === 0) return true

        // If feature is enabled, no need to show upgrade prompt
        if (isFeatureEnabled(featureId)) return false

        return false
    }

    const value = {
        billingInfo: subscription?.plan || null,
        isLoading: false,
        currentPlan: subscription?.plan || null,
        isFeatureEnabled,
        getFeatureLimit,
        shouldShowUpgradePrompt,
    }

    return (
        <BillingContext.Provider value={value}>
            {children}
        </BillingContext.Provider>
    )
}

export const useBilling = () => {
    const context = useContext(BillingContext)
    if (context === undefined) {
        throw new Error('useBilling must be used within a BillingProvider')
    }
    return context
}
